// Time-of-day parsing shared across the app. Accepts the formats:
//   10:23      → 10:23
//   10:23am    → 10:23
//   10:23pm    → 22:23
//   22:23      → 22:23
//   12:00am    → 00:00
//   12:00pm    → 12:00
//   10:23:41   → 10:23:41 (seconds — written by the timeline when a handle
//                          snaps to a neighbor's exact boundary)
// Trailing/leading whitespace is tolerated. "a"/"p" suffixes are accepted as
// shorthand for am/pm. The model uses Time.zone.parse server-side, which
// accepts the same forms; this is just for client-side validation and label
// generation.

export interface ParsedTime {
  valid: boolean
  hour: number      // 0-23
  minute: number    // 0-59
  second: number    // 0-59 (0 unless the input carried seconds)
  formatted: string // "HH:MM" zero-padded 24h
}

const INVALID: ParsedTime = { valid: false, hour: -1, minute: -1, second: -1, formatted: "" }

export function parseTimeOfDay(input: string | null | undefined): ParsedTime {
  if (input == null) return INVALID
  const match = /^\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm|a|p)?\s*$/i.exec(input)
  if (!match) return INVALID

  let hour = parseInt(match[1], 10)
  const minute = parseInt(match[2], 10)
  const second = match[3] ? parseInt(match[3], 10) : 0
  if (minute > 59 || second > 59) return INVALID

  const meridian = match[4]?.toLowerCase()?.[0] // 'a' | 'p' | undefined
  if (meridian) {
    if (hour < 1 || hour > 12) return INVALID
    if (meridian === "a" && hour === 12) hour = 0
    if (meridian === "p" && hour !== 12) hour += 12
  } else {
    if (hour > 23) return INVALID
  }

  return {
    valid: true,
    hour,
    minute,
    second,
    formatted: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
  }
}

// Formatter for a user-friendly label like "4:24 pm" or "10:23 am".
export function formatTimeOfDay(input: string): string | null {
  const parsed = parseTimeOfDay(input)
  if (!parsed.valid) return null
  const ap = parsed.hour < 12 ? "am" : "pm"
  let h = parsed.hour % 12
  if (h === 0) h = 12
  return `${h}:${String(parsed.minute).padStart(2, "0")} ${ap}`
}
